import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { getWsUrl } from '../lib/api';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const WSContext = createContext(null);

export function WSProvider({ children }) {
  const { user } = useAuth();
  const wsRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const listenersRef = useRef(new Set());
  const [presence, setPresence] = useState({}); // user_id -> {online, last_seen}

  const send = useCallback((data) => {
    if (wsRef.current && wsRef.current.readyState === 1) {
      wsRef.current.send(JSON.stringify(data));
    }
  }, []);

  const subscribe = useCallback((fn) => {
    listenersRef.current.add(fn);
    return () => listenersRef.current.delete(fn);
  }, []);

  useEffect(() => {
    if (!user) return;
    let reconnectTimer;
    let alive = true;

    const connect = () => {
      const ws = new WebSocket(getWsUrl());
      wsRef.current = ws;
      ws.onopen = () => setConnected(true);
      ws.onclose = () => {
        setConnected(false);
        if (alive) reconnectTimer = setTimeout(connect, 2000);
      };
      ws.onerror = (ev) => {
        console.warn('[ws] error:', ev);
        try { ws.close(); } catch (e) { console.warn('[ws] close on error failed:', e?.message || e); }
      };
      ws.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data);
          if (data.type === 'presence') {
            setPresence((p) => ({ ...p, [data.user_id]: { online: data.online, last_seen: data.last_seen } }));
          }
          if (data.type === 'new_content' && !user.is_admin) {
            const dot = data.server === 'prime' ? '🟡' : '🔵';
            toast(data.title || 'Nuovi contenuti disponibili', {
              description: `${dot} Vai in Novità Contenuti per vedere l'aggiornamento.`,
              duration: 6000,
            });
            if ('Notification' in window && Notification.permission === 'granted' && document.hidden) {
              try { new Notification('Golden TV Connect', { body: data.title, icon: '/logo.png' }); }
              catch (e) { console.warn('[ws] Notification (content):', e?.message || e); }
            }
          }
          if (data.type === 'broadcast' && !user.is_admin) {
            toast(data.title || 'Nuova comunicazione', {
              description: '📢 ' + (data.preview || 'Apri Comunicazioni Importanti per leggere.'),
              duration: 6000,
            });
            if ('Notification' in window && Notification.permission === 'granted' && document.hidden) {
              try { new Notification(data.title || 'Golden TV Connect', { body: data.preview || 'Nuova comunicazione importante', icon: '/logo.png' }); }
              catch (e) { console.warn('[ws] Notification (broadcast):', e?.message || e); }
            }
          }
          listenersRef.current.forEach((fn) => fn(data));
        } catch (e) {
          console.warn('[ws] onmessage parse failed:', e?.message || e);
        }
      };
    };

    connect();
    const ping = setInterval(() => send({ type: 'ping' }), 25000);

    return () => {
      alive = false;
      clearInterval(ping);
      clearTimeout(reconnectTimer);
      if (wsRef.current) try { wsRef.current.close(); } catch (e) { console.warn('[ws] cleanup close:', e?.message || e); }
    };
  }, [user, send]);

  return (
    <WSContext.Provider value={{ connected, send, subscribe, presence }}>
      {children}
    </WSContext.Provider>
  );
}

export const useWS = () => useContext(WSContext);
